import React, { useEffect, useState } from "react";
import Title from "../common/Title";
import { CiTextAlignJustify } from "react-icons/ci";
import Utils from "../../utils";
import { useLocation, useNavigate } from "react-router-dom";
import { Form, Button, Spinner, Alert, InputGroup } from "react-bootstrap";
import { clientVariableLineActions } from "../../actions/clientVariableLine.actions";
import { clientVariableLineService } from "../../services/clientVariableLine.service";
import { useDispatch, useSelector } from "react-redux";
import SkeletonForm from "../common/SkeletonForm";
import useClientVariableTitle from "../../hooks/clientVariableTitle";
import Subtitle from "../common/Subtitle";
import { LuVariable } from "react-icons/lu";
import { clientVariableActions } from "../../actions/clientVariable.actions";
import { useToast } from "../../contexts/ToastContext";
import PicklistValueSelect from "./PicklistValueSelect";
import EditPicklistValuesModal from "./EditPicklistValuesModal";

function VariableLineForm(props) {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [variableLineId, setVariableLineId] = useState("");
  const [variableKey, setVariableKey] = useState("");
  const [variableValue, setVariableValue] = useState("");
  const [variableId, setVariableId] = useState("");
  const [variableNumber, setVariableNumber] = useState("");
  const [clientId, setClientId] = useState("");
  const [submitted, setSubmitted] = useState(false);

  // #2608: picklist values are owned by this one line (no separate, reusable, named Picklist),
  // and saved independently of the main Key/Value save via EditPicklistValuesModal -- "Add
  // Picklist"/"Edit Picklist" are their own actions, not bundled into the form's Save button.
  // Only meaningful once the line already exists (editing), since creating a line doesn't hand
  // back its new VariableLineId to attach values to yet.
  const [linePicklistValues, setLinePicklistValues] = useState([]);
  const [showEditPicklistModal, setShowEditPicklistModal] = useState(false);

  // #2610: which input control to render for this line's value (Text/Picklist/Date) -- the
  // value itself is always a plain string either way. Local-only, same as picklist values,
  // saved independently of the main Key/Value save as soon as it's changed.
  const [lineType, setLineType] = useState("Text");

  // #2618: whether this line's value can be overridden for a single manual execution (see
  // #2619) without changing the stored value. Local-only, saved independently as soon as
  // it's toggled.
  const [isRuntimeOverridable, setIsRuntimeOverridable] = useState(false);
  const { clientVariableTitle, clientVariableTitleLoading } =
    useClientVariableTitle(variableId, props.user);

  const clientVariableLineReducer = useSelector(
    (state) => state.clientVariableLine
  );

  const clientVariableReducer = useSelector((state) => state.clientVariable);

  useEffect(() => {
    let queryParams = Utils.getQueryStringParams(location);

    queryParams["clientId"] = props.user.clientId;
    queryParams["clientGuid"] = props.user.clientGuid;

    if (queryParams["variableLineId"]) {
      setVariableLineId(queryParams["variableLineId"]);
      dispatch(clientVariableLineActions.get(queryParams));
    } else {
      setVariableLineId("");
      setVariableKey("");
      setVariableValue("");
      setVariableId(queryParams["variableId"]);
      setVariableNumber("");
      setClientId("");

      dispatch(clientVariableActions.get(queryParams));
    }
    // eslint-disable-next-line
  }, [location]);

  useEffect(() => {
    if (
      variableLineId &&
      clientVariableLineReducer.paginatedResult &&
      clientVariableLineReducer.paginatedResult.data &&
      clientVariableLineReducer.paginatedResult.data[0]
    ) {
      const data = clientVariableLineReducer.paginatedResult.data[0];
      setVariableId(data.variableId || "");
      setVariableNumber(data.variableNumber || "");
      setVariableKey(data.variableKey || "");
      setVariableValue(data.variableValue || "");
      setClientId(data.clientId || "");
    }
    // eslint-disable-next-line
  }, [clientVariableLineReducer]);

  const refreshPicklistValues = () => {
    if (variableLineId) {
      clientVariableLineService.getPicklistValues({ variableLineId }).then(
        (response) => setLinePicklistValues(response.data || []),
        () => setLinePicklistValues([])
      );
    }
  };

  const refreshLineType = () => {
    if (variableLineId) {
      clientVariableLineService.getLineType({ variableLineId }).then(
        (response) => setLineType(response.data || "Text"),
        () => setLineType("Text")
      );
    }
  };

  const refreshRuntimeOverridable = () => {
    if (variableLineId) {
      clientVariableLineService.getRuntimeOverridable({ variableLineId }).then(
        (response) => setIsRuntimeOverridable(!!response.data),
        () => setIsRuntimeOverridable(false)
      );
    }
  };

  useEffect(() => {
    refreshPicklistValues();
    refreshLineType();
    refreshRuntimeOverridable();
    // eslint-disable-next-line
  }, [variableLineId]);

  const handleLineTypeChange = (newType) => {
    const previousType = lineType;
    setLineType(newType);
    clientVariableLineService
      .setLineType({
        variableLineId,
        lineType: newType,
        clientId: props.user.clientId !== "" ? props.user.clientId : clientId,
        clientGuid: props.user.clientGuid,
      })
      .then(
        () => showToast("Value type updated successfully", "success"),
        () => {
          setLineType(previousType);
          showToast("Failed to update value type", "danger");
        }
      );
  };

  const handleRuntimeOverridableChange = (newValue) => {
    const previousValue = isRuntimeOverridable;
    setIsRuntimeOverridable(newValue);
    clientVariableLineService
      .setRuntimeOverridable({
        variableLineId,
        isRuntimeOverridable: newValue,
        clientId: props.user.clientId !== "" ? props.user.clientId : clientId,
        clientGuid: props.user.clientGuid,
      })
      .then(
        () => showToast("Runtime override setting updated successfully", "success"),
        () => {
          setIsRuntimeOverridable(previousValue);
          showToast("Failed to update runtime override setting", "danger");
        }
      );
  };

  useEffect(() => {
    if (
      variableId &&
      clientVariableReducer.paginatedResult &&
      clientVariableReducer.paginatedResult.data &&
      clientVariableReducer.paginatedResult.data[0]?.variableName
    ) {
      setVariableNumber(
        clientVariableReducer.paginatedResult.data[0].variableNumber
      );
      setClientId(clientVariableReducer.paginatedResult.data[0].clientId);
    }
    // eslint-disable-next-line
  }, [clientVariableReducer]);

  useEffect(() => {
    // Only act on success if we actually saved something (had a variable key)
    if (!clientVariableLineReducer.success || !variableKey) return;

    if (!variableLineId && clientVariableLineReducer.createdLine) {
      // #2607: Create now returns the created line for real (it used to proxy to the Master
      // Runbook Service and discard the response) -- switch straight into editing it instead
      // of navigating away, so a value type (picklist, date) can be set in the same flow.
      showToast("Variable line created successfully", "success");
      setVariableLineId(clientVariableLineReducer.createdLine.variableLineId);
    } else {
      showToast("Variable line saved successfully", "success");
      navigate('/variable');
    }
    // eslint-disable-next-line
  }, [clientVariableLineReducer.success]);

  const handleSaveVariableLine = () => {
    setSubmitted(true);

    if (variableKey) {
      if (variableLineId) {
        dispatch(
          clientVariableLineActions.update({
            variableId,
            variableLineId,
            variableKey,
            variableValue,
            clientId:
              props.user.clientId !== "" ? props.user.clientId : clientId,
            clientGuid: props.user.clientGuid,
          })
        );
      } else if (variableId) {
        dispatch(
          clientVariableLineActions.create({
            variableId,
            variableNumber,
            variableKey,
            variableValue,
            clientId:
              props.user.clientId !== "" ? props.user.clientId : clientId,
            clientGuid: props.user.clientGuid,
          })
        );
      }
    }
  };

  const operation = !variableLineId ? "Add" : "Update";
  const loading =
    clientVariableTitleLoading || clientVariableLineReducer.loading;

  return (
    <>
      <Title
        text={`${operation} Client Variable Line`}
        icon={<CiTextAlignJustify className="mb-1" />}
      />

      {!loading && (
        <Subtitle
          text={clientVariableTitle}
          icon={<LuVariable className="mb-1" />}
        />
      )}

      {loading && <SkeletonForm fields={4} />}

      {!loading && !clientVariableLineReducer.success && (
        <>
          {clientVariableLineReducer.error && (
            <Alert variant="danger">
              An error has occurred. Please try again later.
            </Alert>
          )}

          <Form>
            {variableLineId && (
              <Form.Group controlId="formVariableLineId" className="mb-3">
                <Form.Label>Variable Line ID</Form.Label>
                <Form.Control
                  type="text"
                  value={variableLineId}
                  disabled
                />
              </Form.Group>
            )}

            <Form.Group controlId="formVariableKey" className="mb-3">
              <Form.Label>Variable Key *</Form.Label>
              <Form.Control
                type="text"
                value={variableKey}
                onChange={(e) => setVariableKey(e.target.value)}
                className={submitted && !variableKey ? "is-invalid" : ""}
                placeholder="Enter variable key"
              />
              <Form.Control.Feedback type="invalid">
                Please provide a variable key.
              </Form.Control.Feedback>
            </Form.Group>

            {variableLineId && (
              <Form.Group controlId="formLineType" className="mb-3">
                <Form.Label>Value Type</Form.Label>
                <Form.Select
                  value={lineType}
                  onChange={(e) => handleLineTypeChange(e.target.value)}
                >
                  <option value="Text">Text</option>
                  <option value="Picklist">Picklist</option>
                  <option value="Date">Date</option>
                </Form.Select>
              </Form.Group>
            )}

            {variableLineId && (
              <Form.Group controlId="formRuntimeOverridable" className="mb-3">
                <Form.Check
                  type="checkbox"
                  label="Allow this value to be overridden for a single manual run"
                  checked={isRuntimeOverridable}
                  onChange={(e) => handleRuntimeOverridableChange(e.target.checked)}
                />
                <Form.Text className="text-muted">
                  The override only applies to that one execution -- the stored value here is never changed by it.
                </Form.Text>
              </Form.Group>
            )}

            <Form.Group controlId="formVariableValue" className="mb-3">
              <Form.Label>Variable Value</Form.Label>
              {lineType === "Picklist" ? (
                <PicklistValueSelect
                  value={variableValue}
                  onChange={setVariableValue}
                  options={linePicklistValues}
                  placeholder="Select a value"
                />
              ) : lineType === "Date" ? (
                <InputGroup>
                  <Form.Control
                    type="date"
                    value={Utils.displayDateToIso(variableValue)}
                    onChange={(e) => setVariableValue(Utils.isoDateToDisplay(e.target.value))}
                  />
                  <Button
                    variant="outline-secondary"
                    disabled={!variableValue}
                    onClick={() => setVariableValue("")}
                    title="Clear date"
                  >
                    ×
                  </Button>
                </InputGroup>
              ) : (
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={variableValue}
                  onChange={(e) => setVariableValue(e.target.value)}
                  placeholder="Enter variable value"
                />
              )}
              {variableLineId && lineType === "Picklist" && (
                <div className="mt-2">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setShowEditPicklistModal(true)}
                  >
                    {linePicklistValues.length > 0 ? "Edit Picklist" : "Add Picklist Values"}
                  </Button>
                </div>
              )}
              {!variableLineId && (
                <Form.Text className="text-muted">
                  You can set a value type (picklist, date) after saving it.
                </Form.Text>
              )}
            </Form.Group>

            <div className="mt-4">
              <Button
                variant="outline-secondary"
                onClick={() => navigate(-1)}
                className="me-2"
              >
                Cancel
              </Button>

              <Button
                variant="primary"
                onClick={handleSaveVariableLine}
                disabled={clientVariableLineReducer.loading}
              >
                {clientVariableLineReducer.loading ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Saving...
                  </>
                ) : (
                  `${operation} Variable Line`
                )}
              </Button>
            </div>
          </Form>

          {variableLineId && lineType === "Picklist" && (
            <EditPicklistValuesModal
              show={showEditPicklistModal}
              onHide={() => setShowEditPicklistModal(false)}
              variableLineId={variableLineId}
              clientId={props.user.clientId !== "" ? props.user.clientId : clientId}
              clientGuid={props.user.clientGuid}
              initialValues={
                linePicklistValues.length > 0
                  ? linePicklistValues
                  : variableValue
                    ? [{ value: variableValue, displayOrder: 0 }]
                    : []
              }
              onSaved={refreshPicklistValues}
            />
          )}
        </>
      )}
    </>
  );
}

export default VariableLineForm;
